import config from '../../config/config';

import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';
import putRequest from './base/put';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Fetching penalty management account data
   * @param query - The query parameters for fetching data
   * @returns - The result of the get request
   */
  fetchPenaltyManagementAccount: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-management`, {
      ...parameter,
    });
  },

  /**
   * Adding penalty override
   * @param query - The data for adding override
   * @returns - The result of the post request
   */
  addOverride: (query: QueryParameters) => {
    const parameter = { ...query };
    return postRequest(`${config.commonApiUrl}api/facility/v1/penalty-management`, {
      ...parameter,
    });
  },

  /**
   * Updating single account status
   * @param id - The account ID to update
   * @returns The result of the patch request
   */
  updateSingleAccountStatus: (id: string, status: boolean) => {
    return patchRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/${id}/status`, {
      status,
    });
  },

  /**
   * View single account details
   * @param id - The account ID to view
   * @returns The result of the get request
   */
  viewSingleAccountDetails: (id: string | null | undefined) => {
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/${id}`);
  },

  /**
   * Update single account details
   * @param id - The account ID to update
   * @param query - The data to update
   * @returns The result of the put request
   */
  updateSingleAccountDetails: (id: string, query: QueryParameters) => {
    const parameters = { ...query };
    return patchRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/${id}`, {
      ...parameters,
    });
  },

  /**
   * Export penalty management account data
   * @param query - The query parameters for exporting data
   * @returns - The result of the get request
   */
  accountExport: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/export`, {
      ...parameter,
    });
  },

  /**
   * Fetch penalty management account filters list
   * @param query - The query parameters for fetching filters
   * @returns - The result of the get request
   */
  accountFiltersList: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/filter-list`, {
      ...parameter,
    });
  },

  /**
   * add Global Penalty Settings
   */
  createGlobalPenalty: (data: any): Promise<any> => {
    const parameters = { ...data };
    return putRequest(`${config.commonApiUrl}api/facility/v1/penalty-global-setting`, parameters);
  },

  /**
   * Fetching request's approved list
   * @param query - The query parameters for fetching data
   */
  fetchRequestApproved: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit };
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/facility-request`, {
      ...parameters,
      requestStatus: 'approved',
    });
  },

  /**
   * Fetching request's approved filters
   */
  fetchRequestFilters: () => {
    return getRequest(
      `${config.commonApiUrl}api/facility/v1/penalty-management/facility-request/filter-list`,
    );
  },

  /**
   * Fetching request's pending list
   * @param query - The query parameters for fetching data
   */
  fetchRequestPending: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit };
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/facility-request`, {
      ...parameters,
      requestStatus: 'pending',
    });
  },

  /**
   * Fetching request's rejected list
   * @param query -n The query parameters for fetching data
   */
  fetchRequestRejected: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit };
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/facility-request`, {
      ...parameters,
      requestStatus: 'rejected',
    });
  },

  /**
   * create approved and rejected permission Api
   */
  createApprovedRejectedPermission: (data: any): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(
      `${config.commonApiUrl}api/facility/v1/penalty-management/request-status`,
      parameters,
    );
  },
  /**
   * Fetch global penalty settings quick view
   * @returns - The result of the get request
   */
  globalSettingQuickView: (): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-global-setting`);
  },

  /**
   * Fetching global setting data
   * @returns - returning global setting's data
   */
  viewGlobalSettings: (): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/facility/v1/penalty-global-setting/new`);
  },

  /**
   * Fetch transaction list
   * @param query
   * @returns
   */
  fetchTransactionList: (query: QueryParameters): Promise<any> => {
    const parameter = { ...query };
    return getRequest(`${config.transactionApiUrl}api/facility/v1/penalty-management/transaction-list`, {
      ...parameter,
    });
  },
  /**
   * Fetch transaction list filters
   * @param query
   * @returns
   */
  fetchTransactionListFilters: () => {
    return getRequest(
      `${config.transactionApiUrl}api/facility/v1/penalty-management/transaction-list/filter-list`,
    );
  },
  /**
   *
   * Export facility request data
   * @param query
   * @returns
   */
  requestExport: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/facility/v1/penalty-management/facility-request-export`,
      {
        ...parameter,
      },
    );
  },
  /**
   * Exporting transaction list data
   * @param query
   * @returns
   */
  transactionExport: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/facility/v1/penalty-management/transaction-list/export`,
      {
        ...parameter,
      },
    );
  },
  /**
   * Fetching card data
   * @returns
   */
  fetchCardData: () => {
    return getRequest(
      `${config.transactionApiUrl}api/facility/v1/penalty-management/transaction-price-details`,
    );
  },
  /**
   * Resubmitting rejected account
   * @param query
   */
  rejectedResubmit: (id: string, query: QueryParameters) => {
    const parameter = { ...query };
    return patchRequest(`${config.commonApiUrl}api/facility/v1/penalty-management/${id}`, {
      ...parameter,
    });
  },

  /**
   * Audit log
   */
  fetchAuditLogList: (id: string, query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/facility/v1/penalty-management/${id}/audit-log`,
      {
        ...parameter,
      },
    );
  },
};
